const getAuthToken = () => {
  return localStorage.getItem('access');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// Most endpoints answer 401 only when the session is gone, so the default is to
// clear it and send the operator back to /login. The login request is the one
// exception: there a 401 means the credentials were wrong, and the redirect
// used to tear the page down before the form could say so — a mistyped password
// silently reloaded the screen with no error at all. Callers opt out with
// { redirectOn401: false } and get the 401 as a normal rejected request.
type RequestOptions = {
  redirectOn401?: boolean;
};

// A dead session usually surfaces as several 401s at once, because a screen
// fires its requests in parallel. Only the first one needs to act: the rest
// would re-clear empty storage and re-assign the same location.
let sessionEnded = false;

// Function to handle 401 responses - logout and redirect
const handle401 = () => {
  if (sessionEnded) return;
  sessionEnded = true;

  // Clear all auth data from localStorage
  localStorage.removeItem('user');
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');

  // Redirect to login page — unless that is already the page in view, where
  // assigning the same URL reloads it and blanks whatever has been typed.
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Helper function to handle API responses with 401 detection
const handleResponse = async (response: Response, { redirectOn401 = true }: RequestOptions = {}) => {
  if (response.status === 401 && redirectOn401) {
    handle401();
    throw new Error('Authentication expired. Please login again.');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Create an error object that preserves the backend error structure
    const error: any = new Error(errorData.detail || 'API request failed');
    error.response = errorData; // Preserve the full error response for field-specific errors
    error.status = response.status;
    throw error;
  }
  
  if (response.status === 204) return null;
  return response.json();
};

export const apiService = {
  get: async (url: string, options?: RequestOptions) => {
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, options);
  },
  
  post: async (url: string, data: any, options?: RequestOptions) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response, options);
  },
  
  put: async (url: string, data: any, options?: RequestOptions) => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response, options);
  },
  
  delete: async (url: string, options?: RequestOptions) => {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, options);
  },
  
  patch: async (url: string, data: any, options?: RequestOptions) => {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response, options);
  },

  // Multipart upload. No Content-Type header: the browser sets it with the
  // boundary. Same 401 handling and error shape as the JSON methods, so an
  // upload failure carries `response.code` like any other request.
  postForm: async (url: string, form: FormData, options?: RequestOptions) => {
    const token = getAuthToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: form,
    });
    return handleResponse(response, options);
  },
}; 